import { ChatPersonalization } from '../../../../domain/chat/models/ChatPersonalization';
import { SavedPersona } from '../../../../domain/chat/models/SavedPersona';
import { SavedPrompt } from '../../../../domain/chat/models/SavedPrompt';
import { BaseService } from '../../../http/BaseHttpService';
import {
    CreateProGPTPersonaRequestDto,
    CreateProGPTPromptRequestDto,
    EditProGPTPersonaRequestDto,
    EditProGPTPromptRequestDto,
} from '../dtos/requests/ProGPTChatPersonalizationRequests';
import { ProGPTResultDto } from '../dtos/responses/ProGPTResultDto';
import {
    ProGPTChatPersonalizationDto,
    ProGPTCreatePersonaResponseDto,
    ProGPTCreatePromptResponseDto,
    ProGPTDeletePersonaResponseDto,
    ProGPTDeletePromptResponseDto,
    ProGPTEditPersonaResponseDto,
    ProGPTEditPromptResponseDto,
} from '../dtos/responses/ProGPTChatPersonalizationDto';
import {
    mapProGPTChatPersonalizationDtoToModel,
    mapProGPTPersonaDtoToModel,
    mapProGPTPromptDtoToModel,
} from '../mappers/ProGPTChatPersonalizationMapper';
import { buildProGPTUrl } from '../../../../features/utils/buildProGPTUrl';

export class ProGPTChatPersonalizationService extends BaseService {
    constructor(private readonly baseUrl: string) {
        super();
    }

    public async getChatPersonalizationAsync(accessToken: string): Promise<ChatPersonalization> {
        const response = await this.getResponseAsync<ProGPTResultDto<ProGPTChatPersonalizationDto>>(
            {
                url: buildProGPTUrl(this.baseUrl, 'ChatPersonalization'),
                method: 'GET',
            },
            accessToken,
        );

        if (!response.isSuccess) {
            throw new Error(response.errors?.join(', ') || 'Failed to load chat personalization.');
        }

        return mapProGPTChatPersonalizationDtoToModel(response.value);
    }

    public async createPersonaAsync(
        request: CreateProGPTPersonaRequestDto,
        accessToken: string,
    ): Promise<SavedPersona> {
        const response = await this.getResponseAsync<ProGPTResultDto<ProGPTCreatePersonaResponseDto>>(
            {
                url: buildProGPTUrl(this.baseUrl, 'ChatPersonalization/personas'),
                method: 'POST',
                body: request,
            },
            accessToken,
        );

        if (!response.isSuccess) {
            throw new Error(response.errors?.join(', ') || 'Failed to create persona.');
        }

        return mapProGPTPersonaDtoToModel(response.value.persona);
    }

    public async editPersonaAsync(
        personaId: string,
        request: EditProGPTPersonaRequestDto,
        accessToken: string,
    ): Promise<SavedPersona> {
        const response = await this.getResponseAsync<ProGPTResultDto<ProGPTEditPersonaResponseDto>>(
            {
                url: buildProGPTUrl(this.baseUrl, `ChatPersonalization/personas/${personaId}`),
                method: 'PATCH',
                body: request,
            },
            accessToken,
        );

        if (!response.isSuccess) {
            throw new Error(response.errors?.join(', ') || 'Failed to edit persona.');
        }

        return mapProGPTPersonaDtoToModel(response.value.persona);
    }

    public async deletePersonaAsync(personaId: string, accessToken: string): Promise<void> {
        const response = await this.getResponseAsync<ProGPTResultDto<ProGPTDeletePersonaResponseDto>>(
            {
                url: buildProGPTUrl(this.baseUrl, `ChatPersonalization/personas/${personaId}`),
                method: 'DELETE',
            },
            accessToken,
        );

        if (!response.isSuccess) {
            throw new Error(response.errors?.join(', ') || 'Failed to delete persona.');
        }
    }

    public async createPromptAsync(
        request: CreateProGPTPromptRequestDto,
        accessToken: string,
    ): Promise<SavedPrompt> {
        const response = await this.getResponseAsync<ProGPTResultDto<ProGPTCreatePromptResponseDto>>(
            {
                url: buildProGPTUrl(this.baseUrl, 'ChatPersonalization/prompts'),
                method: 'POST',
                body: request,
            },
            accessToken,
        );

        if (!response.isSuccess) {
            throw new Error(response.errors?.join(', ') || 'Failed to create prompt.');
        }

        return mapProGPTPromptDtoToModel(response.value.prompt);
    }

    public async editPromptAsync(
        promptId: string,
        request: EditProGPTPromptRequestDto,
        accessToken: string,
    ): Promise<SavedPrompt> {
        const response = await this.getResponseAsync<ProGPTResultDto<ProGPTEditPromptResponseDto>>(
            {
                url: buildProGPTUrl(this.baseUrl, `ChatPersonalization/prompts/${promptId}`),
                method: 'PATCH',
                body: request,
            },
            accessToken,
        );

        if (!response.isSuccess) {
            throw new Error(response.errors?.join(', ') || 'Failed to edit prompt.');
        }

        return mapProGPTPromptDtoToModel(response.value.prompt);
    }

    public async deletePromptAsync(promptId: string, accessToken: string): Promise<void> {
        const response = await this.getResponseAsync<ProGPTResultDto<ProGPTDeletePromptResponseDto>>(
            {
                url: buildProGPTUrl(this.baseUrl, `ChatPersonalization/prompts/${promptId}`),
                method: 'DELETE',
            },
            accessToken,
        );

        if (!response.isSuccess) {
            throw new Error(response.errors?.join(', ') || 'Failed to delete prompt.');
        }
    }
}
